/**
 * ModalPortal — wraps any modal/dialog/overlay so it renders at
 * document.body instead of wherever the caller is mounted in the
 * React tree (Round L-37, Daniel 2026-06-27).
 *
 * Why this exists: TR's Layout wraps page content in <main
 * className="tr-page-anim"> which runs a CSS keyframe animating
 * `transform`. An element with a non-`none` `transform` creates a
 * new containing block for descendants with `position: fixed` — so
 * any modal mounted inside <main> ends up scoped to <main> instead
 * of the viewport. Backdrop covers only the page area; sidebar/header
 * stay reachable; on the contact-detail page the backdrop only
 * covered the left aside column.
 *
 * Was first hit on AddActivityModal back in L-23 (memory:
 * project_modals_must_portal.md). 22 other modals had the same
 * latent bug — none had triggered it yet because the page-in
 * animation finishes in 260ms and most modals are opened well after
 * that. But the same trap fires for ANY future ancestor that adds
 * transform/filter/perspective/contain, so the safe move is to
 * portal everything to body.
 *
 * Usage:
 *
 *   return (
 *     <ModalPortal>
 *       <div style={{ position: 'fixed', inset: 0, ... }}>...</div>
 *     </ModalPortal>
 *   );
 *
 * SSR-safe via the typeof document check — TR is client-only today
 * but the guard costs nothing and keeps future SSR painless.
 */
import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';

export function ModalPortal({ children }: { children: ReactNode }) {
  if (typeof document === 'undefined') return <>{children}</>;
  return createPortal(children, document.body);
}
